"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Shield, ArrowRight, ArrowLeft, Mail, FolderOpen, Users, Calendar,
  MessageSquare, Database, Server, Cloud, Building2, Lock, CheckCircle2,
  Clock, Globe, Scale, Layers, RefreshCw, HardDrive, Network
} from "lucide-react";
import lasuiteLogoSrc from "@/assets/lasuite-logo.png";
import keycloakLogoSrc from "@/assets/keycloak-logo.png";
import zimbraLogoSrc from "@/assets/zimbra-logo.webp";
import nextcloudLogoSrc from "@/assets/nextcloud-logo.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ContactSalesModal from "@/components/ContactSalesModal";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

const SovereignContinuity = () => {
  const { t } = useTranslation();
  const [salesModalOpen, setSalesModalOpen] = useState(false);

  const whyBullets = [
    { icon: Mail, key: "sc.why.b1" },
    { icon: FolderOpen, key: "sc.why.b2" },
    { icon: MessageSquare, key: "sc.why.b3" },
    { icon: Users, key: "sc.why.b4" },
    { icon: Database, key: "sc.why.b5" },
  ];

  const ecosystems: Array<{ icon?: React.ComponentType<{ className?: string }>; logo?: string | { src: string }; key: string }> = [
    { logo: lasuiteLogoSrc, key: "sc.eco.lasuite" },
    { logo: keycloakLogoSrc, key: "sc.eco.keycloak" },
    { logo: zimbraLogoSrc, key: "sc.eco.zimbra" },
    { logo: nextcloudLogoSrc, key: "sc.eco.nextcloud" },
    { icon: Server, key: "sc.eco.custom" },
    { icon: HardDrive, key: "sc.eco.ovh" },
    { icon: Building2, key: "sc.eco.onprem" },
  ];

  const hostingOptions = [
    { icon: Building2, titleKey: "sc.hosting.onprem", descKey: "sc.hosting.onpremDesc" },
    { icon: Cloud, titleKey: "sc.hosting.ovh", descKey: "sc.hosting.ovhDesc" },
    { icon: Lock, titleKey: "sc.hosting.private", descKey: "sc.hosting.privateDesc" },
    { icon: Network, titleKey: "sc.hosting.hybrid", descKey: "sc.hosting.hybridDesc" },
  ];

  const comparisonTraditional = [
    "sc.compare.trad1",
    "sc.compare.trad2",
    "sc.compare.trad3",
  ];

  const comparisonResilience = [
    "sc.compare.res1",
    "sc.compare.res2",
    "sc.compare.res3",
    "sc.compare.res4",
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ContactSalesModal open={salesModalOpen} onOpenChange={setSalesModalOpen} />

      <main>
        {/* HERO */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/40" />
          <div className="container mx-auto px-4 md:px-8 relative">
            <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("sc.backHome")}
              </Link>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-muted-foreground text-xs font-medium mb-6 border border-border">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                {t("sc.heroBadge")}
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-foreground leading-[1.12] tracking-tight mb-6">
                {t("sc.heroTitle")}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-10">
                {t("sc.heroSubtitle")}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" className="gap-2 text-sm font-semibold px-6" onClick={() => setSalesModalOpen(true)}>
                  {t("sc.heroCta1")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="lg" className="gap-2 text-sm font-semibold px-6" asChild>
                  <a href="#scenarios">{t("sc.heroCta2")}</a>
                </Button>
              </div>
            </motion.div>

            {/* Diagram: M365 → Vault → EU */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="max-w-2xl mx-auto mt-16"
            >
              <div className="flex items-center justify-center gap-2 md:gap-4">
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-card border border-border flex items-center justify-center shadow-sm">
                    <Cloud className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground text-center">Microsoft 365</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-px w-8 md:w-16 bg-primary/30" />
                  <ArrowRight className="h-5 w-5 text-primary flex-shrink-0" />
                </div>
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shadow-sm">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <span className="text-xs font-medium text-primary text-center">Resilience365 Vault</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-px w-8 md:w-16 bg-primary/30" />
                  <ArrowRight className="h-5 w-5 text-primary flex-shrink-0" />
                </div>
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center shadow-sm">
                    <Globe className="h-8 w-8 text-accent" />
                  </div>
                  <span className="text-xs font-medium text-accent text-center">{t("sc.diagramEu")}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* WHY THIS MATTERS */}
        <section className="py-20 md:py-28 bg-secondary">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                {t("sc.whyTitle")}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t("sc.whySubtitle")}
              </p>
            </motion.div>
            <div className="max-w-xl mx-auto space-y-3">
              {whyBullets.map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="flex items-center gap-4 rounded-lg border border-border bg-background p-4 shadow-sm"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 flex-shrink-0">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-medium text-foreground text-sm">{t(item.key)}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* COMPATIBLE EUROPEAN ECOSYSTEMS */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                {t("sc.ecoTitle")}
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-lg mx-auto">
                {t("sc.ecoSubtitle")}
              </p>
            </motion.div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {ecosystems.map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-6 shadow-sm text-center"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                    {item.logo ? (
                      <img src={typeof item.logo === "string" ? item.logo : item.logo.src} alt={t(item.key)} className="h-8 w-8 object-contain" />
                    ) : item.icon ? (
                      <item.icon className="h-6 w-6 text-primary" />
                    ) : null}
                  </div>
                  <span className="font-medium text-foreground text-sm">{t(item.key)}</span>
                </motion.div>
              ))}
            </div>
            <motion.p {...fadeUp} className="text-center text-xs text-muted-foreground mt-8">
              {t("sc.ecoNote")}
            </motion.p>
          </div>
        </section>

        {/* PRA SCENARIOS */}
        <section id="scenarios" className="py-20 md:py-28 bg-secondary">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                {t("sc.scenariosTitle")}
              </h2>
              <p className="text-muted-foreground">{t("sc.scenariosSubtitle")}</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {/* SME */}
              <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0 }}>
                <Card className="h-full border-border">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      <h3 className="font-display font-bold text-foreground">{t("sc.sme.title")}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("sc.sme.users")}</p>
                    <ul className="space-y-2">
                      {["sc.sme.s1", "sc.sme.s2", "sc.sme.s3", "sc.sme.s4"].map((k) => (
                        <li key={k} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                          {t(k)}
                        </li>
                      ))}
                    </ul>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs font-medium text-muted-foreground italic">{t("sc.sme.positioning")}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Mid-Market */}
              <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }}>
                <Card className="h-full border-border">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-primary" />
                      <h3 className="font-display font-bold text-foreground">{t("sc.mid.title")}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("sc.mid.users")}</p>
                    <ul className="space-y-2">
                      {["sc.mid.s1", "sc.mid.s2", "sc.mid.s3", "sc.mid.s4"].map((k) => (
                        <li key={k} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                          {t(k)}
                        </li>
                      ))}
                    </ul>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs font-medium text-muted-foreground italic">{t("sc.mid.positioning")}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Tier 3 Sovereign */}
              <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.2 }}>
                <Card className="h-full border-primary/30 bg-gradient-to-b from-primary/5 to-card relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      <h3 className="font-display font-bold text-foreground">{t("sc.tier3.title")}</h3>
                    </div>
                    <p className="text-xs font-semibold text-primary">{t("sc.tier3.headline")}</p>
                    <ul className="space-y-2">
                      {["sc.tier3.s1", "sc.tier3.s2", "sc.tier3.s3", "sc.tier3.s4", "sc.tier3.s5"].map((k) => (
                        <li key={k} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          {t(k)}
                        </li>
                      ))}
                    </ul>
                    <Button size="sm" className="w-full gap-2 mt-2" onClick={() => setSalesModalOpen(true)}>
                      {t("sc.tier3.cta")} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* HOSTING OPTIONS */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                {t("sc.hostingTitle")}
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto">{t("sc.hostingSubtitle")}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
              {hostingOptions.map((opt, i) => (
                <motion.div
                  key={opt.titleKey}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6 text-center space-y-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 mx-auto">
                        <opt.icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-display font-bold text-foreground text-sm">{t(opt.titleKey)}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{t(opt.descKey)}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* COMPARISON */}
        <section className="py-20 md:py-28 bg-secondary">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                {t("sc.compareTitle")}
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Traditional */}
              <motion.div {...fadeUp}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <h3 className="font-display font-bold text-foreground mb-4 flex items-center gap-2">
                      <RefreshCw className="h-5 w-5 text-muted-foreground" />
                      {t("sc.compare.tradTitle")}
                    </h3>
                    <ul className="space-y-3">
                      {comparisonTraditional.map((k) => (
                        <li key={k} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                          {t(k)}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Resilience365 */}
              <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }}>
                <Card className="h-full border-primary/30">
                  <CardContent className="p-6">
                    <h3 className="font-display font-bold text-primary mb-4 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      Resilience365
                    </h3>
                    <ul className="space-y-3">
                      {comparisonResilience.map((k) => (
                        <li key={k} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                          {t(k)}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                {t("sc.ctaTitle")}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8 max-w-lg mx-auto">
                {t("sc.ctaSubtitle1")}{" "}
                <span className="text-primary font-semibold">{t("sc.ctaSubtitle2")}</span>
              </p>
              <Button size="lg" className="gap-2 text-sm font-semibold px-8" onClick={() => setSalesModalOpen(true)}>
                {t("sc.ctaButton")}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <p className="text-xs text-muted-foreground mt-4">{t("sc.ctaNote")}</p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SovereignContinuity;
